import React from 'react'
import cx from 'classnames'
import { useEntry, useVisibilityTrigger } from 'intersection-observer'
import { useDevice } from 'device'
import { usePathname, useSearchParams } from 'router'
import links from 'links'
import { useAb } from 'hooks'
import { constants } from 'helpers'
import loadable from '@loadable/component'

import { useUser } from 'modules/user'
import { Bone } from 'components/layout'
import ProductsInQueueProvider from 'containers/ProductsInQueueProvider/ProductsInQueueProvider'

import ContinueStickyBar from 'compositions/landings/ContinueStickyBar/ContinueStickyBar'

import HeroSection, { type HeroSectionProps } from './components/HeroSection/HeroSection'
import BrandsRow from './components/BrandsRow/BrandsRow'
import BrandsSection from './components/BrandsSection/BrandsSection'
import PressSection from './components/PressSection/PressSection'
import BestsellersSection from './components/BestsellersSection/BestsellersSection'
import BottomBanner from './components/BottomBanner/BottomBanner'

import messages from './messages'


const QuizSectionAb = loadable(
  () => import('./components/QuizSectionAb/QuizSectionAb'),
  { fallback: <Bone className="bg-light-beige" pw={100} h={656} /> }
)
const QuizSection = loadable(
  () => import('./components/QuizSection/QuizSection'),
  { fallback: <Bone className="bg-light-beige" pw={100} h={656} /> }
)

const HowItWorksSectionAb = loadable(
  () => import('./components/HowItWorksSectionAb/HowItWorksSectionAb'),
  { fallback: <Bone className="bg-light-beige" pw={100} h={656} /> }
)
const HowItWorksSection = loadable(
  () => import('./components/HowItWorksSection/HowItWorksSection'),
  { fallback: <Bone className="bg-light-beige" pw={100} h={656} /> }
)

const ScentbirdSelectBlock = loadable(
  () => import('compositions/ScentbirdSelectBlock/ScentbirdSelectBlock'),
  { fallback: <Bone className="bg-light-beige" pw={100} h={1000} /> }
)

export type HomePageViewProps = {
  isFetching: boolean
  price: string
  eventDiscountText?: SubscriptionModule.EventDiscountText
  offerType?: SubscriptionModule.OfferType
  discount?: string
  priceWithDiscount?: string
  extraShippingPrice?: string
  couponCode?: string
  heroCustomTexts?: HeroSectionProps['customTexts']
  heroCustomTextStyles?: HeroSectionProps['customTextStyles']
  heroCustomTimer?: HeroSectionProps['customTimer']
  heroCustomLogoImage?: string
}

const View: React.FunctionComponent<HomePageViewProps> = (props) => {
  const {
    couponCode,
    discount,
    eventDiscountText,
    extraShippingPrice,
    heroCustomTexts,
    heroCustomTextStyles,
    heroCustomTimer,
    heroCustomLogoImage,
    isFetching,
    offerType,
    price,
    priceWithDiscount,
  } = props

  const { isMobile } = useDevice()
  const { user } = useUser()

  const defaultSectionMargin = isMobile ? 'mt-64' : 'mt-80'

  const pathname = usePathname()
  const [ { utm_campaign: utmCampaign } ] = useSearchParams()

  const isHomePage = (
    pathname === links.home
    || pathname === links.homeMen
  )
  const isTimerEnabled = (
    isHomePage
    || Boolean(heroCustomTimer)
  )

  const isCanada = (
    pathname === links.freeTrialCa
    || pathname === links.freeTrialMenCa
    || Boolean(utmCampaign?.match(/canada/i))
  )

  const isStartButtonWithPriceEnabled = (
    offerType === 'discount'
    && isHomePage
    && !isCanada
  )

  const isHomeCopyUpdateAbEnabled = useAb(constants.abTests.homeCopyUpdate) === 'b'
  const isHomeSubscriptionOfferBadgeEnabled = useAb(constants.abTests.homeSubscriptionOfferBadge) === 'b'
  const isHomeSelectBlockEnabled = useAb(constants.abTests.homeSelectBlock) === 'b'

  const isHomeQuizSectionAbEnabled = useAb(constants.abTests.homeQuizSectionBottlesAnimation) === 'b'
  const hasScentProfile = Boolean(user?.analyticsMetadata?.hasScentProfile)
  // TODO: Show old quiz section for now if user has scent profile
  // Update new section design for users with scent profile if CRO-259 wins
  const isHomeQuizSectionAbVisible = isHomeQuizSectionAbEnabled && !hasScentProfile

  // TODO: Remove this tracking when CRO-259 test is over
  const quizSectionRef = useVisibilityTrigger(() => {
    // TODO: Uncomment the tracking when CRO-259 is launched
    // track('Home quiz block display')
  })

  const isNewHowItWorksEnabled = useAb(constants.abTests.homeNewHowItWorks) === 'b'
  // TODO: Remove this tracking when CRO-189 is over
  const howItWorksRef = useVisibilityTrigger(() => {
    // TODO: Uncomment the tracking when CRO-189 is launched
    // track('Home case banner display')
  })

  // narrow rootMargin to tiny line to hide StickyBar before global footer
  const [ stickyBarAreaRef, stickyBarAreaEntry ] = useEntry({ observerProps: { rootMargin: isHomeSelectBlockEnabled ? '-10% 0% -90%' : '0% 0% -90%' } })
  const isStickyBarVisible = stickyBarAreaEntry?.isIntersecting

  const startButtonWithPriceMessage = {
    ...messages.getStartedForPrice, values: { price: priceWithDiscount },
  }
  const startButtonWithCrossedPriceMessage = {
    ...messages.getStartedForOriginalPriceCrossed, values: { price, priceWithDiscount },
  }
  const startButtonWithCrossedPriceGoldMessage = {
    ...messages.getStartedForPriceCrossedGold, values: { price, priceWithDiscount },
  }
  const tryForPriceMessage = {
    ...messages.tryForPrice, values: { price: priceWithDiscount },
  }

  let heroStartButtonMessage = startButtonWithPriceMessage
  if (isHomeSubscriptionOfferBadgeEnabled) {
    heroStartButtonMessage = startButtonWithCrossedPriceMessage
  }
  else if (isNewHowItWorksEnabled) {
    heroStartButtonMessage = startButtonWithCrossedPriceGoldMessage
  }

  return (
    <ProductsInQueueProvider>
      <HeroSection
        isFetching={isFetching}
        offerType={offerType}
        discount={discount}
        price={price}
        priceWithDiscount={priceWithDiscount}
        extraShippingPrice={extraShippingPrice}
        customTexts={heroCustomTexts}
        customTextStyles={heroCustomTextStyles}
        customTimer={heroCustomTimer}
        customLogoImage={heroCustomLogoImage}
        isTimerEnabled={isTimerEnabled}
        startButtonMessage={isStartButtonWithPriceEnabled ? heroStartButtonMessage : undefined}
        // Improve and use the code of passing customTexts to Hero instead of separate custom note message
        noteMessage={isHomeSubscriptionOfferBadgeEnabled ? undefined : isStartButtonWithPriceEnabled && !isMobile ? null : undefined}
      />
      <BrandsRow />
      <div className={defaultSectionMargin} ref={howItWorksRef} />
      {
        isNewHowItWorksEnabled ? (
          <HowItWorksSectionAb startButtonMessage={isStartButtonWithPriceEnabled ? startButtonWithCrossedPriceGoldMessage : undefined} />
        ) : (
          <HowItWorksSection
            startButtonMessage={isStartButtonWithPriceEnabled && isMobile ? startButtonWithPriceMessage : undefined}
          />
        )
      }
      <BrandsSection
        className={isMobile ? isNewHowItWorksEnabled ? null : 'mt-72' : 'mt-80'}
        startButtonMessage={isHomeCopyUpdateAbEnabled && isStartButtonWithPriceEnabled ? startButtonWithPriceMessage : undefined}
      />
      <div className={isMobile ? null : defaultSectionMargin} ref={quizSectionRef} />
      {
        isHomeQuizSectionAbVisible ? (
          <QuizSectionAb />
        ) : (
          <QuizSection />
        )
      }
      <PressSection className={defaultSectionMargin} />
      <div ref={stickyBarAreaRef}>
        <BestsellersSection className={defaultSectionMargin} />
        {
          isHomeSelectBlockEnabled && (
            <ScentbirdSelectBlock
              className={cx(defaultSectionMargin, isMobile ? 'pb-16' : 'pb-40')}
              title={messages.selectBlockTitle}
              description={messages.selectBlockDescription}
              productsCarouselType={isMobile ? 'small' : 'detailed'}
              withBrowseButton={false}
              withNonAuthRedirect={false}
              withoutLink
            />
          )
        }
      </div>
      <BottomBanner
        className={isHomeSelectBlockEnabled ? undefined : defaultSectionMargin}
        startButtonMessage={isHomeCopyUpdateAbEnabled && isStartButtonWithPriceEnabled ? startButtonWithPriceMessage : undefined}
      />
      <ContinueStickyBar
        isVisible={isStickyBarVisible}
        eventDiscountText={eventDiscountText}
        continueButtonMessage={isHomeCopyUpdateAbEnabled && isStartButtonWithPriceEnabled ? tryForPriceMessage : undefined}
        coupon={couponCode}
      />
    </ProductsInQueueProvider>
  )
}

export default View
